import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import _ from 'lodash';

import { Flex, Box, Text, Image, Icon, ICON_LIST } from 'Components/Base';
import Breadcrumb from 'Components/Breadcrumb';
import Loading from 'Components/Loading';
import { getFilterData, convertToBreadCrumb } from 'Containers/AttendanceFilter';
import IndividualUnitReportData from 'Domains/IndividualUnitReportData';
import AttendanceFilterData from 'Domains/AttendanceFilterData';
import Criteria from 'Components/Criteria';
import { SYLLABUS_MODE } from 'Domains/syllabus';
import { COLOR } from 'Components/Theme';
import Tooltip from 'Components/Tooltip';

import IndividualUnitAttendanceTable from './IndividualUnitAttendanceTable';

const SchoolUnitReportTableWrapper = styled(Box)`
  position: relative;
  overflow: auto;
  & > div {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: visible;
  }
`;

const useQueryString = () => {
  return new URLSearchParams(useLocation().search);
};

const IndividualUnitAttendanceTableView = ({
  currentFilter,
  data,
  translation,
  setDisabledFetch,
  mode,
}) => {
  if (!_.isEmpty(currentFilter)) {
    setDisabledFetch(false);
    if (!_.isEmpty(data)) {
      const reportData =
        mode === SYLLABUS_MODE.TRADITIONAL
          ? data.foIndividualUnitReport.reports
          : data.foTKIndividualUnitReport.reports;
      return <IndividualUnitAttendanceTable data={reportData} mode={mode} />;
    } else {
      return (
        <IndividualUnitAttendanceTable
          data={[]}
          renderEmpty={({ colSpan }) => (
            <tr>
              <td colSpan={colSpan}>
                <Box bg={'tableRowBg'} py={9}>
                  <Text style={{ textAlign: 'center' }}>{translation('NOT_FOUND')}</Text>
                </Box>
              </td>
            </tr>
          )}
        />
      );
    }
  } else {
    return (
      <IndividualUnitAttendanceTable
        data={[]}
        renderEmpty={({ colSpan }) => (
          <tr>
            <td colSpan={colSpan}>
              <Box bg={'tableRowBg'}>
                <Flex flexDirection={'column'} py={9}>
                  <Flex mb={3} mx={'auto'}>
                    <Text>{translation('SELECT_BUTTON')}</Text>
                  </Flex>
                  <Flex mb={3} mx={'auto'}>
                    <Image
                      maxHeight={'40px'}
                      src={'/assets/components/Icons/filter-button-icon.png'}
                      srcSet={
                        '/assets/components/Icons/filter-button-icon.png 1x, /assets/components/Icons/filter-button-icon@2x.png 2x, /assets/components/Icons/filter-button-icon@3x.png 3x'
                      }
                    />
                  </Flex>
                  <Flex mb={3} mx={'auto'}>
                    <Text>{translation('TO_DISPLAYED')}</Text>
                  </Flex>
                </Flex>
              </Box>
            </td>
          </tr>
        )}
      />
    );
  }
};

const IndividualUnit = ({
  isFetchData,
  setIsFetchData,
  setDisabledFetch,
  onFilterApplied,
  schoolCode,
  username,
}) => {
  const { t } = useTranslation('individualUnit');
  const queryString = useQueryString();
  const [currentFilter, setCurrentFilter] = useState({});
  const [isFirstTime, setIsFirstTime] = useState(true);

  const filterValue = {
    term: currentFilter?.term?.id ?? queryString.get('term') ?? null,
    grade: currentFilter?.grade?.id ?? queryString.get('grade') ?? null,
    classNo: currentFilter?.classNo?.classNo ?? queryString.get('class') ?? null,
    subject: currentFilter?.subject?.id ?? queryString.get('subj') ?? null,
    syllabus: currentFilter?.syllabus?.courseCode ?? queryString.get('course') ?? null,
    mode: currentFilter?.mode ?? queryString.get('mode') ?? SYLLABUS_MODE.TRADITIONAL,
  };

  return (
    <>
      <Box>
        <AttendanceFilterData initialValue={filterValue} schoolCode={schoolCode}>
          {({ list, value, updateValue }) => {
            const { term, grade, classNo, subject, syllabus } = value;
            const { termList, gradeList, classList, subjectList, syllabusList, lessonList } = list;
            if (queryString.toString() !== '' && isFirstTime) {
              const filterData = getFilterData(list, value);

              if (termList && gradeList && classList && subjectList && syllabusList && lessonList) {
                if (term && grade && classNo && subject && syllabus) {
                  onFilterApplied(filterData, filterValue);
                  setCurrentFilter(filterData);
                } else if (term && grade && classNo && subject && syllabus) {
                  onFilterApplied(filterData, filterValue);
                  setCurrentFilter(filterData);
                }
                setIsFirstTime(false);
              }
            }

            return (
              <>
                <Text.Title>{t('STUDENT_INFO')}</Text.Title>
                <Breadcrumb
                  maxItemsPerRow={6}
                  items={convertToBreadCrumb(currentFilter)}
                  p={0}
                  my={2}
                />
                {/* Quiz criteria is traditional-only; the video progress legend applies to both modes. */}
                <Flex mt={5} alignItems={'center'}>
                  {filterValue.mode === SYLLABUS_MODE.TRADITIONAL && (
                    <Criteria
                      title={t('QUIZ_CRITERIA')}
                      values={[
                        { text: t('GOOD'), color: 'success' },
                        { text: t('NORMAL'), color: 'warning' },
                        { text: t('BAD'), color: 'dangerAlt' },
                      ]}
                    />
                  )}
                  <Tooltip
                    arrow
                    placement="right-start"
                    title={
                      <>
                        <Text
                          style={{
                            fontFamily: 'Noto Sans Thai',
                            fontSize: '12px',
                            whiteSpace: 'pre-wrap',
                            fontWeight: 700,
                          }}
                        >
                          {t('PROGRESS_LEGEND_TITLE')}
                        </Text>
                        <Text style={{ fontFamily: 'Noto Sans Thai', fontSize: '12px' }}>
                          {t('PROGRESS_LEGEND_COMPLETED')}
                        </Text>
                        <Text style={{ fontFamily: 'Noto Sans Thai', fontSize: '12px' }}>
                          {t('PROGRESS_LEGEND_IN_PROGRESS')}
                        </Text>
                        <Text style={{ fontFamily: 'Noto Sans Thai', fontSize: '12px' }}>
                          {t('PROGRESS_LEGEND_INCOMPLETED')}
                        </Text>
                      </>
                    }
                  >
                    <Flex
                      ml={filterValue.mode === SYLLABUS_MODE.TRADITIONAL ? 4 : 0}
                      alignItems="center"
                    >
                      <Icon name={ICON_LIST.INFO_CIRCLE} size={20} color={COLOR.primary} />
                      <Text
                        ml={1}
                        fontWeight={600}
                        color="primary"
                        style={{
                          borderBottom: `1px solid ${COLOR.primary}`,
                          cursor: 'default',
                        }}
                      >
                        {t('PROGRESS_LEGEND_TITLE')}
                      </Text>
                    </Flex>
                  </Tooltip>
                </Flex>
              </>
            );
          }}
        </AttendanceFilterData>
      </Box>
      <SchoolUnitReportTableWrapper>
        <IndividualUnitReportData value={filterValue} schoolCode={schoolCode} username={username}>
          {({ loading, error, data, refetch }) => {
            if (loading) return <Loading />;
            if (error) return null;
            if (isFetchData) {
              refetch();
              setIsFetchData(false);
            }
            return (
              <IndividualUnitAttendanceTableView
                currentFilter={currentFilter}
                data={data}
                translation={t}
                setDisabledFetch={setDisabledFetch}
                mode={filterValue.mode}
              />
            );
          }}
        </IndividualUnitReportData>
      </SchoolUnitReportTableWrapper>
    </>
  );
};

IndividualUnit.defaultProps = {
  onFilterApplied: {},
};

export default IndividualUnit;
